import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import i18n from '@/i18n'
import { canonicalManager } from '@/utils/canonicalManager'
import { preventDevUrlLeak } from '@/middleware/seoMiddleware'
import { trackPageView } from '@/utils/analytics'

// 支援的語言列表
const supportedLocales = ['zh-TW', 'zh-CN', 'en', 'hi', 'ja', 'ko']
const defaultLocale = 'zh-TW'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: `/${defaultLocale}`
  },
  {
    path: '/:locale(zh-TW|zh-CN|en|hi|ja|ko)',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/Home.vue'),
        meta: {
          title: 'seo.home.title',
          description: 'seo.home.description'
        }
      },
      {
        path: 'tools',
        name: 'Tools',
        component: () => import('@/views/Tools.vue'),
        meta: {
          title: 'tools.title',
          description: 'tools.description'
        }
      },
      {
        path: 'ranking',
        name: 'ToolsRanking',
        component: () => import('@/views/ToolsRanking.vue'),
        meta: {
          title: 'ranking.title',
          description: 'ranking.description'
        }
      },
      {
        path: 'tools/json-formatter',
        name: 'JsonFormatter',
        component: () => import('@/tools/JsonFormatter.vue'),
        meta: {
          title: 'tools.jsonFormatter.title',
          description: 'tools.jsonFormatter.description'
        }
      },
      {
        path: 'tools/base64-encoder',
        name: 'Base64Encoder',
        component: () => import('@/tools/Base64Encoder.vue'),
        meta: {
          title: 'tools.base64Encoder.title',
          description: 'tools.base64Encoder.description'
        }
      },
      {
        path: 'tools/url-encoder',
        name: 'UrlEncoder',
        component: () => import('@/tools/UrlEncoder.vue'),
        meta: {
          title: 'tools.urlEncoder.title',
          description: 'tools.urlEncoder.description'
        }
      },
      {
        path: 'tools/hash-generator',
        name: 'HashGenerator',
        component: () => import('@/tools/HashGenerator.vue'),
        meta: {
          title: 'tools.hashGenerator.title',
          description: 'tools.hashGenerator.description'
        }
      },
      {
        path: 'tools/api-tester',
        name: 'ApiTester',
        component: () => import('@/tools/ApiTester.vue'),
        meta: {
          title: 'tools.apiTester.title',
          description: 'tools.apiTester.description'
        }
      },
      {
        path: 'tools/graphql-validator',
        name: 'GraphqlValidator',
        component: () => import('@/tools/GraphqlValidator.vue'),
        meta: {
          title: 'tools.graphqlValidator.title',
          description: 'tools.graphqlValidator.description'
        }
      },
      {
        path: 'tools/swagger-generator',
        name: 'SwaggerGenerator',
        component: () => import('@/tools/SwaggerGenerator.vue'),
        meta: {
          title: 'tools.swaggerGenerator.title',
          description: 'tools.swaggerGenerator.description'
        }
      },
      {
        path: 'tools/jwt-decoder',
        name: 'JwtDecoder',
        component: () => import('@/tools/JwtDecoder.vue'),
        meta: {
          title: 'tools.jwtDecoder.title',
          description: 'tools.jwtDecoder.description'
        }
      },
      {
        path: 'tools/qr-generator',
        name: 'QrGenerator',
        component: () => import('@/tools/QrGenerator.vue'),
        meta: {
          title: 'tools.qrGenerator.title',
          description: 'tools.qrGenerator.description'
        }
      },
      {
        path: 'tools/image-converter',
        name: 'ImageConverter',
        component: () => import('@/tools/ImageConverter.vue'),
        meta: {
          title: 'tools.imageConverter.title',
          description: 'tools.imageConverter.description'
        }
      },
      {
        path: 'tools/svg-viewer',
        name: 'SvgViewer',
        component: () => import('@/views/tools/SvgViewer.vue'),
        meta: {
          title: 'tools.svgViewer.title',
          description: 'tools.svgViewer.description'
        }
      },
      {
        path: 'tools/csv-processor',
        name: 'CsvProcessor',
        component: () => import('@/tools/CsvProcessor.vue'),
        meta: {
          title: 'tools.csvProcessor.title',
          description: 'tools.csvProcessor.description'
        }
      },
      {
        path: 'tools/xml-formatter',
        name: 'XmlFormatter',
        component: () => import('@/tools/XmlFormatter.vue'),
        meta: {
          title: 'tools.xmlFormatter.title',
          description: 'tools.xmlFormatter.description'
        }
      },
      {
        path: 'tools/crypto-tool',
        name: 'CryptoTool',
        component: () => import('@/views/tools/CryptoTool.vue'),
        meta: {
          title: 'tools.cryptoTool.title',
          description: 'tools.cryptoTool.description'
        }
      },
      // === 網路工具套件 ===
      {
        path: 'tools/network-ping',
        name: 'NetworkPing',
        component: () => import('@/tools/NetworkPing.vue'),
        meta: {
          title: 'tools.networkPing.title',
          description: 'tools.networkPing.description'
        }
      },

      {
        path: 'tools/dns-lookup',
        name: 'DnsLookup',
        component: () => import('@/tools/DnsLookup.vue'),
        meta: {
          title: 'tools.dnsLookup.title',
          description: 'tools.dnsLookup.description'
        }
      },
      {
        path: 'tools/ssl-checker',
        name: 'SslChecker',
        component: () => import('@/views/tools/SslChecker.vue'),
        meta: {
          title: 'tools.sslChecker.title',
          description: 'tools.sslChecker.description'
        }
      },
      {
        path: 'tools/ascii-table',
        name: 'AsciiTable',
        component: () => import('@/views/tools/AsciiTable.vue'),
        meta: {
          title: 'tools.asciiTable.title',
          description: 'tools.asciiTable.description'
        }
      },
      {
        path: 'tools/uuid-generator',
        name: 'UuidGenerator',
        component: () => import('@/views/tools/UuidGenerator.vue'),
        meta: {
          title: 'tools.uuidGenerator.title',
          description: 'tools.uuidGenerator.description'
        }
      },
      {
        path: 'tools/markdown-previewer',
        name: 'MarkdownPreviewer',
        component: () => import('@/views/tools/MarkdownPreviewer.vue'),
        meta: {
          title: 'tools.markdownPreviewer.title',
          description: 'tools.markdownPreviewer.description'
        }
      },
      // === 新增工具 ===
      {
        path: 'tools/regex-tester',
        name: 'RegexTester',
        component: () => import('@/tools/RegexTester.vue'),
        meta: {
          title: 'tools.regexTester.title',
          description: 'tools.regexTester.description'
        }
      },
      {
        path: 'tools/timestamp-converter',
        name: 'TimestampConverter',
        component: () => import('@/tools/TimestampConverter.vue'),
        meta: {
          title: 'tools.timestampConverter.title',
          description: 'tools.timestampConverter.description'
        }
      },
      {
        path: 'tools/http-status-codes',
        name: 'HttpStatusCodes',
        component: () => import('@/tools/HttpStatusCodes.vue'),
        meta: {
          title: 'tools.httpStatusCodes.title',
          description: 'tools.httpStatusCodes.description'
        }
      },
      {
        path: 'tools/cron-parser',
        name: 'CronParser',
        component: () => import('@/tools/CronParser.vue'),
        meta: {
          title: 'tools.cronParser.title',
          description: 'tools.cronParser.description'
        }
      },
      {
        path: 'tools/json-schema-validator',
        name: 'JsonSchemaValidator',
        component: () => import('@/tools/JsonSchemaValidator.vue'),
        meta: {
          title: 'tools.jsonSchemaValidator.title',
          description: 'tools.jsonSchemaValidator.description'
        }
      },
      {
        path: 'tools/json-yaml-converter',
        name: 'JsonYamlConverter',
        component: () => import('@/tools/JsonYamlConverter.vue'),
        meta: {
          title: 'tools.jsonYamlConverter.title',
          description: 'tools.jsonYamlConverter.description'
        }
      },
      {
        path: 'tools/ip-address-tool',
        name: 'IpAddressTool',
        component: () => import('@/tools/IpAddressTool.vue'),
        meta: {
          title: 'tools.ipAddressTool.title',
          description: 'tools.ipAddressTool.description'
        }
      },
      {
        path: 'tools/html-entity-encoder',
        name: 'HtmlEntityEncoder',
        component: () => import('@/tools/HtmlEntityEncoder.vue'),
        meta: {
          title: 'tools.htmlEntityEncoder.title',
          description: 'tools.htmlEntityEncoder.description'
        }
      },
      {
        path: 'tools/color-converter',
        name: 'ColorConverter',
        component: () => import('@/tools/ColorConverter.vue'),
        meta: {
          title: 'tools.colorConverter.title',
          description: 'tools.colorConverter.description'
        }
      },
      {
        path: 'tools/lorem-ipsum-generator',
        name: 'LoremIpsumGenerator',
        component: () => import('@/tools/LoremIpsumGenerator.vue'),
        meta: {
          title: 'tools.loremIpsumGenerator.title',
          description: 'tools.loremIpsumGenerator.description'
        }
      },
      {
        path: 'tools/diff-tool',
        name: 'DiffTool',
        component: () => import('@/tools/DiffTool.vue'),
        meta: {
          title: 'tools.diffTool.title',
          description: 'tools.diffTool.description'
        }
      },
      {
        path: 'tools/curl-converter',
        name: 'CurlConverter',
        component: () => import('@/tools/CurlConverter.vue'),
        meta: {
          title: 'tools.curlConverter.title',
          description: 'tools.curlConverter.description'
        }
      },
      {
        path: 'tools/mock-api-generator',
        name: 'MockApiGenerator',
        component: () => import('@/tools/MockApiGenerator.vue'),
        meta: {
          title: 'tools.mockApiGenerator.title',
          description: 'tools.mockApiGenerator.description'
        }
      },
      {
        path: 'tools/websocket-tester',
        name: 'WebSocketTester',
        component: () => import('@/tools/WebSocketTester.vue'),
        meta: {
          title: 'tools.webSocketTester.title',
          description: 'tools.webSocketTester.description'
        }
      },
      // === 新增進階工具 ===
      {
        path: 'tools/js-css-minifier',
        name: 'JsCssMinifier',
        component: () => import('@/tools/JsCssMinifier.vue'),
        meta: {
          title: 'tools.jsCssMinifier.title',
          description: 'tools.jsCssMinifier.description'
        }
      },
      {
        path: 'tools/sql-formatter',
        name: 'SqlFormatter',
        component: () => import('@/tools/SqlFormatter.vue'),
        meta: {
          title: 'tools.sqlFormatter.title',
          description: 'tools.sqlFormatter.description'
        }
      },
      {
        path: 'tools/password-generator',
        name: 'PasswordGenerator',
        component: () => import('@/tools/PasswordGenerator.vue'),
        meta: {
          title: 'tools.passwordGenerator.title',
          description: 'tools.passwordGenerator.description'
        }
      },
      {
        path: 'tools/text-counter',
        name: 'TextCounter',
        component: () => import('@/tools/TextCounter.vue'),
        meta: {
          title: 'tools.textCounter.title',
          description: 'tools.textCounter.description'
        }
      },
      {
        path: 'tools/number-base-converter',
        name: 'NumberBaseConverter',
        component: () => import('@/tools/NumberBaseConverter.vue'),
        meta: {
          title: 'tools.numberBaseConverter.title',
          description: 'tools.numberBaseConverter.description'
        }
      },
      {
        path: 'tools/json-to-typescript',
        name: 'JsonToTypescript',
        component: () => import('@/tools/JsonToTypescript.vue'),
        meta: {
          title: 'tools.jsonToTypescript.title',
          description: 'tools.jsonToTypescript.description'
        }
      },
      {
        path: 'tools/json-path-query',
        name: 'JsonPathQuery',
        component: () => import('@/tools/JsonPathQuery.vue'),
        meta: {
          title: 'tools.jsonPathQuery.title',
          description: 'tools.jsonPathQuery.description'
        }
      },
      {
        path: 'about',
        name: 'About',
        component: () => import('@/views/About.vue'),
        meta: {
          title: 'about.title',
          description: 'about.subtitle'
        }
      },
      {
        path: 'privacy',
        name: 'Privacy',
        component: () => import('@/views/Privacy.vue'),
        meta: {
          title: 'privacy.title',
          description: 'privacy.subtitle'
        }
      },
      {
        path: 'terms',
        name: 'Terms',
        component: () => import('@/views/Terms.vue'),
        meta: {
          title: 'terms.title',
          description: 'terms.subtitle'
        }
      },
      // 廣告測試頁面
      {
        path: 'ad-test',
        name: 'AdTest',
        component: () => import('@/views/AdTest.vue'),
        meta: { 
          title: '廣告測試',
          description: '廣告系統測試與調試頁面'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: `/${defaultLocale}`
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 設置語言的統一函數 - 優化為非阻塞
function setLanguage(locale: string) {
  if (!supportedLocales.includes(locale)) {
    return false
  }
  
  // 使用非阻塞方式設置語言
  setTimeout(() => {
    try {
      // 設置 i18n 語言
      const globalI18n = i18n.global as any
      if (globalI18n.locale?.value) {
        globalI18n.locale.value = locale
      } else if (globalI18n.locale) {
        globalI18n.locale = locale
      } else {
        throw new Error("i18n.global.locale is not defined")
      }
      
      // 更新 HTML lang 屬性
      document.documentElement.lang = locale
      
      // 非阻塞地同步到 localStorage
      try {
        localStorage.setItem('preferred-language', locale)
      } catch (error) {
        console.warn('無法保存語言偏好設定:', error)
      }
      
    } catch (error) {
      console.error('Failed to set language:', error)
    }
  }, 0)
  
  return true
}

// 非阻塞方式獲取用戶偏好語言
function getUserPreferredLanguage(): string {
  try {
    return localStorage.getItem('preferred-language') || navigator.language
  } catch (error) {
    console.warn('無法讀取語言偏好設定:', error)
    return navigator.language
  }
}

// 防止開發環境 URL 洩漏
preventDevUrlLeak()

// 路由守衛 - 大幅簡化，避免阻塞
router.beforeEach((to, _from, next) => {
  // 1. 基本語言驗證（非阻塞）
  const locale = to.params.locale as string
  const supportedLocales = ['zh-TW', 'zh-CN', 'en', 'hi', 'ja', 'ko']
  
  // 檢查語言有效性
  if (!locale || !supportedLocales.includes(locale)) {
    // 如果 URL 中沒有有效語言，則重定向到預設語言
    let bestLocale = defaultLocale
    
    try {
      const userLang = getUserPreferredLanguage()
      bestLocale = supportedLocales.find(l => userLang.startsWith(l.split('-')[0])) || defaultLocale
    } catch (error) {
      console.warn('語言偏好設定檢查失敗，使用預設語言:', error)
    }
    
    // 保持原始路徑和查詢參數
    const newPath = `/${bestLocale}${to.fullPath.replace(/^\/[^/]+/, '')}`
    
    if (to.fullPath !== newPath) {
      return next(newPath)
    }
  }
  
  // 2. 檢查並防止雙重語言代碼（簡化版）
  const pathSegments = to.path.split('/').filter(segment => segment)
  const duplicateLocaleCheck = pathSegments.filter(segment => 
    supportedLocales.includes(segment)
  )
  
  if (duplicateLocaleCheck.length > 1) {
    // 檢測到雙重語言代碼，重定向到第一個有效的語言代碼
    const validLocale = duplicateLocaleCheck[0]
    const remainingPath = to.path
      .replace(new RegExp(`/(${supportedLocales.join('|')})/`, 'g'), '/')
      .replace(/\/+/g, '/')
    const redirectPath = `/${validLocale}${remainingPath === '/' ? '' : remainingPath}`
    
    console.warn(`檢測到雙重語言代碼: ${to.path} -> ${redirectPath}`)
    return next(redirectPath)
  }
  
  // 3. 設置語言（非阻塞）
  if (locale && supportedLocales.includes(locale)) {
    setLanguage(locale)
  }

  // 4. 立即繼續路由，不等待其他操作
  next()
})

// 在路由完成後處理 SEO（非阻塞）
router.afterEach((to, from) => {
  if (to.path === from.path) return

  // 使用 requestIdleCallback 或 setTimeout 來處理 SEO 操作
  const handleSEO = () => {
    try {
      const locale = to.params.locale as string
      
      // 1. 動態標題和 Meta (i18n)
      const titleKey = to.meta.title as string
      const descriptionKey = to.meta.description as string

      if (titleKey) {
        const globalI18n = i18n.global as any
        document.title = globalI18n.t(titleKey)
      }
      
      // 更新 meta description
      const descriptionEl = document.querySelector('meta[name="description"]')
      if (descriptionEl && descriptionKey) {
        const globalI18n = i18n.global as any
        descriptionEl.setAttribute('content', globalI18n.t(descriptionKey))
      }
      
      // 2. SEO 管理 (Canonical, Hreflang) - 非阻塞
      setTimeout(() => {
        try {
          canonicalManager.setCanonicalURL(to.fullPath, locale)
          canonicalManager.setHreflangTags(to.fullPath)
        } catch (error) {
          console.warn('設置 SEO 標籤時發生錯誤:', error)
        }
      }, 100)
      
      // 3. Google Analytics 頁面追蹤 - 非阻塞
      setTimeout(() => {
        try {
          const globalI18n = i18n.global as any
          const pageTitle = titleKey ? globalI18n.t(titleKey) : document.title
          trackPageView(to.fullPath, pageTitle)
        } catch (error) {
          console.warn('頁面追蹤失敗:', error)
        }
      }, 200)
      
    } catch (error) {
      console.warn('路由後處理失敗:', error)
    }
  }
  
  // 使用 requestIdleCallback 在瀏覽器空閒時處理
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(handleSEO, { timeout: 1000 })
  } else {
    setTimeout(handleSEO, 0)
  }
})

export default router